import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { clearInFlight, getPending, getVersion, isConnecting, isInFlight, markInFlight, setPending, subscribe, } from './slot.js';
const TOAST_DURATION_MS = 5000;
const CANCEL = { kind: 'cancel' };
const FLUSH = { kind: 'send', note: null, via: 'toast' };
function optionsOf(entry) {
    return entry.owner.current.options;
}
function cleanNote(note) {
    const trimmed = note?.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed;
}
/** closing goes true BEFORE dismiss: the toast library fires onDismiss for a programmatic dismiss too. */
function closeToast(entry) {
    entry.closing = true;
    if (entry.toastId == null) {
        return;
    }
    const id = entry.toastId;
    entry.toastId = null;
    optionsOf(entry).toaster.dismiss(id);
}
/** A throwing consumer callback must not skip later ones or reject a `void` call site: report it and carry on. */
function guard(callback) {
    try {
        callback();
    }
    catch (error) {
        console.error('useDeferredConnect: consumer callback threw', error);
    }
}
async function deliver(entry, note, via) {
    const { target } = entry;
    const messageLength = note?.length ?? 0;
    let result;
    markInFlight(target.userId);
    try {
        result = await optionsOf(entry).send(target, note);
    }
    catch {
        const options = optionsOf(entry);
        guard(() => options.toaster.error(options.strings.failed));
        guard(() => options.onSendError?.(target, { messageLength, via }));
        guard(() => options.onRevert?.(target));
        return 'failed';
    }
    finally {
        clearInFlight(target.userId);
    }
    const options = optionsOf(entry);
    const { status } = result;
    guard(() => options.toaster.success(status === 'ACCEPTED' ? options.strings.connected : options.strings.sent));
    guard(() => options.onSendSuccess?.(target, { status, messageLength, via, result }));
    guard(() => options.onSettled?.(target, result));
    return 'sent';
}
/** The single choke point: every send and every cancel of a request runs through here exactly once. */
function settle(entry, outcome) {
    if (entry.settled) {
        return Promise.resolve('skipped');
    }
    entry.settled = true;
    if (getPending() === entry) {
        setPending(null);
    }
    closeToast(entry);
    if (outcome.kind === 'cancel') {
        guard(() => optionsOf(entry).onRevert?.(entry.target));
        return Promise.resolve('skipped');
    }
    return deliver(entry, outcome.note, outcome.via);
}
/**
 * A newer Connect or the owner's unmount sends the request with no note, in either phase.
 * The send is committed first so a throwing consumer callback cannot strand the request.
 * Only a supersede (`notify`) counts as dismissing the note dialog; unmount stays silent.
 */
function flushPending(entry, notify) {
    const owner = entry.owner.current;
    const wasIntro = entry.phase === 'intro';
    void settle(entry, FLUSH);
    if (owner.intro === entry) {
        owner.intro = null;
    }
    if (!wasIntro || !notify) {
        return;
    }
    try {
        owner.options.onIntroDismissed?.(entry.target);
    }
    finally {
        owner.setIntroTarget(null);
    }
}
function supersede() {
    const previous = getPending();
    if (!previous) {
        return;
    }
    flushPending(previous, true);
}
/** Auto-close and swipe/close send; programmatic dismisses are fenced off by `closing`. */
function flushToast(entry) {
    if (getPending() !== entry || entry.phase !== 'toast' || entry.closing || entry.settled) {
        return;
    }
    void settle(entry, FLUSH);
}
function openIntro(entry) {
    if (entry.settled || getPending() !== entry) {
        return;
    }
    entry.phase = 'intro';
    closeToast(entry);
    const owner = entry.owner.current;
    owner.intro = entry;
    owner.setIntroTarget(entry.target);
    owner.options.onIntroOpened?.(entry.target);
}
function open(owner, target) {
    const options = owner.current.options;
    options.onClicked?.(target);
    options.onOptimisticPending?.(target);
    const entry = { owner, target, phase: 'toast', toastId: null, settled: false, closing: false };
    setPending(entry);
    entry.toastId = options.toaster.show(options.strings.pending(target), {
        duration: TOAST_DURATION_MS,
        action: {
            label: options.strings.addNote,
            onClick: () => {
                openIntro(entry);
            },
        },
        cancel: {
            label: options.strings.cancel,
            onClick: () => {
                void settle(entry, CANCEL);
            },
        },
        onAutoClose: () => {
            flushToast(entry);
        },
        onDismiss: () => {
            flushToast(entry);
        },
    });
}
/**
 * Deferred connect: optimistic pending UI, a 5s toast with Add Note / Cancel, an
 * optional note dialog, then one send. One pending request per app runtime; only an
 * explicit Cancel discards it, and every other exit sends it exactly once.
 */
export function useDeferredConnect(options) {
    const [introTarget, setIntroTarget] = useState(null);
    const [introSending, setIntroSending] = useState(false);
    const owner = useRef({ options, setIntroTarget, setIntroSending, intro: null });
    const self = owner;
    const version = useSyncExternalStore(subscribe, getVersion, getVersion);
    // Published on commit, never during render, so a render React throws away cannot
    // leak its options into a settle path. No deps: every commit republishes.
    useLayoutEffect(() => {
        owner.current.options = options;
    });
    useEffect(() => {
        return () => {
            // Flush only the request this instance owns; a sibling's window is left alone.
            const entry = getPending();
            if (entry?.owner !== self) {
                return;
            }
            flushPending(entry, false);
        };
    }, [self]);
    const connect = useCallback((target) => {
        if (getPending()?.target.userId === target.userId || isInFlight(target.userId)) {
            return;
        }
        try {
            supersede();
        }
        finally {
            open(self, target);
        }
    }, [self]);
    const sendIntro = useCallback(async (note) => {
        const entry = self.current.intro;
        if (!entry || entry.settled) {
            return 'skipped';
        }
        setIntroSending(true);
        try {
            return await settle(entry, { kind: 'send', note: cleanNote(note), via: 'intro' });
        }
        finally {
            setIntroSending(false);
            if (self.current.intro === entry) {
                self.current.intro = null;
                setIntroTarget(null);
            }
        }
    }, [self]);
    const cancelIntro = useCallback(() => {
        const entry = self.current.intro;
        if (!entry || entry.settled) {
            return;
        }
        const { options } = self.current;
        self.current.intro = null;
        void settle(entry, CANCEL);
        try {
            options.onIntroDismissed?.(entry.target);
        }
        finally {
            setIntroTarget(null);
        }
    }, [self]);
    // Only hides this instance's dialog; the request stays live for sendIntro/cancelIntro.
    const clearIntro = useCallback(() => {
        setIntroTarget(null);
    }, []);
    // `version` re-creates the reader so memoized consumers see slot changes.
    const isConnectingUser = useCallback((userId) => {
        return isConnecting(userId);
    }, [version]);
    return {
        connect,
        introTarget,
        introSending,
        sendIntro,
        cancelIntro,
        clearIntro,
        isConnecting: isConnectingUser,
    };
}
//# sourceMappingURL=useDeferredConnect.js.map